import type { BadgeRecord, BadgeTarget } from '../../interfaces';

function updateBadgePresentation(
  badge: HTMLButtonElement,
  tooltip: HTMLSpanElement,
  target: BadgeTarget,
  translate: (key: string) => string,
): void {
  const tooltipElement = tooltip;
  const label = translate(target.feature.title);
  badge.setAttribute(
    'aria-label',
    target.feature.ctaLabel
      ? `${label}. ${translate(target.feature.ctaLabel)}`
      : label,
  );
  tooltipElement.textContent = translate(target.tooltip);
}

export function createBadge(
  document: Document,
  host: HTMLElement,
  target: BadgeTarget,
  tooltipId: string,
  translate: (key: string) => string,
  showTooltip: (badge: HTMLButtonElement, tooltip: HTMLSpanElement) => void,
  hideTooltip: (tooltip: HTMLSpanElement) => void,
  onActivate: (event: MouseEvent) => void,
): BadgeRecord {
  const badge = document.createElement('button');
  badge.className = 'feature-announcement-dom-badge';
  badge.type = 'button';
  badge.dataset['featureAnnouncementBadge'] = target.feature.featureKey;

  const icon = document.createElement('i');
  icon.className = 'pi pi-megaphone';
  icon.setAttribute('aria-hidden', 'true');
  badge.appendChild(icon);

  const tooltip = document.createElement('span');
  tooltip.className = 'feature-announcement-dom-badge__tooltip';
  tooltip.id = tooltipId;
  tooltip.setAttribute('role', 'tooltip');
  tooltip.hidden = true;
  Object.assign(tooltip.style, {
    position: 'fixed',
    zIndex: '1000',
    maxWidth: 'min(280px, calc(100vw - 16px))',
    boxSizing: 'border-box',
    transform: 'translateY(-50%)',
  });
  badge.setAttribute('aria-describedby', tooltip.id);

  const onMouseEnter = (): void => showTooltip(badge, tooltip);
  const onFocus = (): void => showTooltip(badge, tooltip);
  const onMouseLeave = (): void => hideTooltip(tooltip);
  const onBlur = (): void => hideTooltip(tooltip);
  const onClick = (event: MouseEvent): void => onActivate(event);
  badge.addEventListener('mouseenter', onMouseEnter);
  badge.addEventListener('focus', onFocus);
  badge.addEventListener('mouseleave', onMouseLeave);
  badge.addEventListener('blur', onBlur);
  badge.addEventListener('click', onClick);

  updateBadgePresentation(badge, tooltip, target, translate);
  document.body.appendChild(tooltip);
  host.appendChild(badge);

  return {
    featureKey: target.feature.featureKey,
    host,
    badge,
    tooltip,
    listeners: [
      () => badge.removeEventListener('mouseenter', onMouseEnter),
      () => badge.removeEventListener('focus', onFocus),
      () => badge.removeEventListener('mouseleave', onMouseLeave),
      () => badge.removeEventListener('blur', onBlur),
      () => badge.removeEventListener('click', onClick),
    ],
  };
}

export function updateBadge(
  record: BadgeRecord,
  target: BadgeTarget,
  translate: (key: string) => string,
): void {
  updateBadgePresentation(record.badge, record.tooltip, target, translate);
}

export function destroyBadge(record: BadgeRecord): void {
  record.listeners.forEach((removeListener) => removeListener());
  record.badge.remove();
  record.tooltip.remove();
}
